import { useEffect, useCallback, useRef } from 'react';

const intercomAppId = 'xtmugxce';
const w: any = window;

type IntercomChatbotWidgetProps = {
  accountId: number;
  email: string;
  locale: string;
  merchantId: string;
  isTrialUser: boolean;
  signUpDate: Date;
};

function loadIntercomScript({ appId }: { appId: string }): void {
  const scriptId = 'shore-intercom';
  if (!document.getElementById(scriptId)) {
    const head = document.querySelector('head');
    const script = document.createElement('script');

    script.id = scriptId;
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    script.onerror = () => script.remove();
    script.onload = () => {
      // Dispatch event when Intercom script is fully loaded
      window.dispatchEvent(new CustomEvent('intercom:ready'));
    };

    if (head) {
      head.appendChild(script);
    }
  }
}

function IntercomChatbotWidget({
  email,
  accountId,
  locale,
  merchantId = '',
  isTrialUser,
  signUpDate,
}: IntercomChatbotWidgetProps) {
  const isScriptLoaded = useRef(false);
  const isInitialized = useRef(false);
  const interactionTimeout = useRef<ReturnType<typeof setTimeout>>();

  const shouldLoadImmediately = locale === 'de';

  // Preconnect to Intercom domains
  useEffect(() => {
    const preconnectLinks = [
      { rel: 'preconnect', href: 'https://widget.intercom.io' },
      { rel: 'preconnect', href: 'https://api-iam.eu.intercom.io' },
      { rel: 'dns-prefetch', href: 'https://widget.intercom.io' },
      { rel: 'dns-prefetch', href: 'https://api-iam.eu.intercom.io' },
    ];

    preconnectLinks.forEach(({ rel, href }) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      document.head.appendChild(link);
    });

    return () => {
      preconnectLinks.forEach(({ rel, href }) => {
        const link = document.querySelector(
          `link[rel="${rel}"][href="${href}"]`
        );
        if (link) link.remove();
      });
    };
  }, []);

  const initializeIntercom = useCallback(() => {
    if (isInitialized.current) return;

    w.intercomSettings = {
      user_id: accountId.toString(),
      app_id: intercomAppId,
      name: '',
      email,
      phone: '',
      api_base: 'https://api-iam.eu.intercom.io',
      alignment: 'left',
      action_color: '#5860d6',
      background_color: '#5860d6',
      merchant_id: merchantId,
      merchant_locale: locale,
      merchant_created_at: signUpDate,
      organization_id: '',
      is_trial_user: isTrialUser,
      account_type: isTrialUser ? 'trial' : 'paid',
      is_key_account: false,
      bo_pos_link: `http://dashboard.inventorum.com/admin/accounts/account/${accountId}`,
      bo_bnm_link: '',
    };

    if (w.Intercom && typeof w.Intercom === 'function') {
      w.Intercom('update', w.intercomSettings);
      isInitialized.current = true;
    } else {
      const intercom = (...args: any) => intercom.c(args);
      intercom.q = [] as any;
      intercom.c = (args: any) => intercom.q.push(args);
      w.Intercom = intercom;

      if (document.readyState === 'complete') {
        loadIntercomScript({ appId: intercomAppId });
      } else {
        w.addEventListener(
          'load',
          () => loadIntercomScript({ appId: intercomAppId }),
          { once: true }
        );
      }

      w.Intercom('boot', w.intercomSettings);
      isInitialized.current = true;
    }
  }, [accountId, email, merchantId, isTrialUser]);

  const loadIntercomOnInteraction = useCallback(() => {
    if (isScriptLoaded.current) return;

    if (interactionTimeout.current) {
      clearTimeout(interactionTimeout.current);
    }

    interactionTimeout.current = setTimeout(
      () => {
        if (!isScriptLoaded.current) {
          loadIntercomScript({ appId: intercomAppId });
          isScriptLoaded.current = true;
          initializeIntercom();
        }
      },
      shouldLoadImmediately ? 0 : 1000
    );
  }, [initializeIntercom, shouldLoadImmediately]);

  useEffect(() => {
    if (!isScriptLoaded.current) {
      if (shouldLoadImmediately) {
        loadIntercomScript({ appId: intercomAppId });
        isScriptLoaded.current = true;
        initializeIntercom();
        return undefined;
      }

      const events = ['mousemove', 'scroll', 'click', 'keydown'];
      const debouncedLoad = () => {
        if (interactionTimeout.current) {
          clearTimeout(interactionTimeout.current);
        }
        interactionTimeout.current = setTimeout(
          loadIntercomOnInteraction,
          1000
        );
      };

      events.forEach((event) => {
        document.addEventListener(event, debouncedLoad, { passive: true });
      });

      const idleTimer = setTimeout(loadIntercomOnInteraction, 10000);

      return () => {
        events.forEach((event) => {
          document.removeEventListener(event, debouncedLoad);
        });
        if (interactionTimeout.current) {
          clearTimeout(interactionTimeout.current);
        }
        clearTimeout(idleTimer);
      };
    }
    return undefined;
  }, [loadIntercomOnInteraction, shouldLoadImmediately, initializeIntercom]);

  useEffect(
    () => () => {
      if (w.Intercom) {
        w.Intercom('shutdown');
      }
      if (interactionTimeout.current) {
        clearTimeout(interactionTimeout.current);
      }
    },
    []
  );

  return null;
}

// eslint-disable-next-line import/prefer-default-export
export { IntercomChatbotWidget };
